/**
 * Client-safe GearMaster service — uses internal API route.
 * Safe to import from client components (no firebase-admin dependency).
 *
 * For server-side usage (getServerSideProps, API routes), import from
 * master.service.server.ts directly for direct Firestore access.
 */
import { MasterDocument } from './master.types';

/**
 * Fetch a GearMaster product by slug or document ID.
 * Uses the internal API route — works in both browser and SSR contexts.
 */
export async function getMasterById(
  idOrSlug: string | undefined | null
): Promise<MasterDocument | null> {
  if (!idOrSlug) return null;

  const baseUrl =
    typeof window !== 'undefined'
      ? '' // Browser: relative URL
      : process.env.NEXT_PUBLIC_VERCEL_URL
      ? `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`
      : 'http://localhost:3000';

  let res: Response;
  try {
    res = await fetch(`${baseUrl}/api/master/${encodeURIComponent(idOrSlug)}`);
  } catch (err) {
    // Network-level failure (offline, DNS, timeout) is transient, same as
    // a 5xx from the route below — throw instead of swallowing to null so
    // callers backed by React Query (ProductCard) can retry instead of
    // caching a false "no match" for the query's staleTime (GB web#1005
    // gate, Codex finding, 2026-08-19 14:56:24Z).
    console.error('[GearMaster] Error fetching master:', err);
    throw err;
  }

  // A confirmed not-found (the route only returns 404 for a clean null
  // from Firestore, never for a caught exception) is the only case that
  // resolves to null. Any other non-OK status — in particular the 503 the
  // route returns when its own Firestore lookup threw — is transient by
  // definition. Throwing here (instead of returning null) lets React
  // Query's retry/error behavior run for callers that need it, rather
  // than collapsing "temporarily unavailable" into the same null as a
  // real not-found.
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(
      `[GearMaster] getMasterById(${idOrSlug}) failed with status ${res.status}`
    );
  }
  return res.json();
}

/**
 * Fetch similar GearMaster products by brand and optionally series.
 * Client-side stub — returns empty array.
 * Server-side usage should import from master.service.server.ts directly.
 */
export async function getSimilarMasters(args: {
  brand: string;
  series?: string;
  excludeId?: string;
}): Promise<MasterDocument[]> {
  return [];
}
